import React from 'react';
import {expenseLineTotal} from "@/Case/Expense/expenseQuantity";
import util from '../../lib/util';
import * as DateUtil from '../../lib/dateutils';
import {formatDuration} from '../../lib/picos/duration';
import {formatFMLFromId, formatUserNameFromId} from '../../Contacts/ContactFormat';
import CaseHelper from '../../Case/CaseHelper';
import LeadHelper from '../../CRM/Sales/LeadHelper';
import * as CampaignHelper from '../../CRM/Marketing/Campaigns/CampaignHelper';
import FinancialHelper from '../../Financial/FinancialHelper';
import EntityMetaHelper from './EntityMetaHelper';
import FormatProcessorPaymentMeta from '../../Financial/PaymentProcessor/FormatProcessorPayment';
import {normalizePaymentMeta} from '../../Financial/PaymentProcessor/normalizePaymentMeta';
import LookupRegistry from '../LookupSelect/LookupRegistry';
import TextField from '../TextField';
import {stripHTML} from '../../lib/picos/textUtils';
import find from '../../store/find';
import store from '../../store/store';
import i18n from '../../i18n/i18n';

import Campaign from '../../entities/campaign';
import Contact from '../../entities/contact';
import Document from '../../entities/document';
import Invoice from '../../entities/invoice';
import Lead from '../../entities/lead';
import Matter from '../../entities/matter';
import Task from '../../entities/task';
import Transaction from '../../entities/transaction';

import type {ReactNode} from 'react';
import type {EntitySchema} from '../../@types/schema';
import type {uuid} from '../../@types/entity';
import type {EntityMetaFilter, EntityMetaFilters, EntityMetaNameMap, EntityMetaTransform, EntityMetaTransforms} from './EntityMeta';
import type {CampaignHardCosts, CampaignRecurringCosts} from '../../entities/campaign';
import type {DispositionType, MatterHourlyRates} from '../../entities/matter';
import type {RateTypeType} from '../../entities/caseType';
import type {TaskNotification, TaskTieIns} from '../../entities/task';
import type Milestone from '../../entities/milestone';
import type Expense from '../../entities/expense';

export type KeyedObjectConfig = {
    nameMap: Record<string, string>;
    textTransforms?: Record<string, (v: any) => string>;
    normalize?: (v: any) => any;
};

export type EntityMetaConfig = {
    entityClass: {
        new(doc: any): any;
        getSchema(): EntitySchema;
        [key: string]: any;
    };
    ignoreFields: string[];
    nameMap: EntityMetaNameMap;
    transforms: EntityMetaTransforms;
    textTransforms: Record<string, (v: any) => string>;
    filters?: EntityMetaFilters;
    keyedObjectFields?: Record<string, KeyedObjectConfig>;
};

const COMMON_IGNORE = ['entityId', 'entityType', 'importSource', 'importId'];

// --- Text transform helpers (plain string output for ChangeDescriber / Activity Stream) ---

export function dispositionText(d: DispositionType): string {
    if (d === 'o') return 'Open';
    if (d === 'p') return 'Pending';
    if (d === 'h') return 'On Hold';
    if (d === 'w') return 'Withdrawn';
    return 'Closed';
}

export function invoiceStatusText(s: string): string {
    if (s === 'D') return 'Draft';
    if (s === 'F') return 'Finalized';
    return s;
}

function sourceTargetTypeText(t: string): string {
    if (t === 'G') return 'Client Trust';
    if (t === 'M') return 'Case Trust';
    if (t === 'B') return 'Operating Account';
    if (t === 'C') return 'Client/Contact';
    if (t === 'E') return 'Escrow';
    if (t === 'O') return 'Operating Account';
    return t || '';
}

function impactUrgencyText(v: string): string {
    if (v === 'L') return 'Low';
    if (v === 'M') return 'Medium';
    if (v === 'H') return 'High';
    return '';
}

function parseRateTypesText(rt: RateTypeType): string {
    if (!rt) return '';
    const result: string[] = [];
    if (rt.includes('h')) result.push('Hourly');
    if (rt.includes('f')) result.push('Flat Fee');
    if (rt.includes('c')) result.push('Contingency');
    return result.join(', ');
}

function getSourceTargetText(id: uuid): string {
    const contact = store.getState().contacts.get(id);
    if (contact) return formatFMLFromId(id);

    const matter = store.getState().matters.get(id);
    if (matter) return matter.name;

    const ba = store.getState().bankAccounts.get(id);
    if (ba) return ba.name;

    return id || '';
}

function formatExpenseText(expenseId: uuid): string {
    const expense = find<Expense>('expenses', expenseId);
    if (!expense) return 'Expense Not Found';

    const payee = formatFMLFromId(expense.payee);
    const expenseDate = DateUtil.formatDate(expense.expenseDate);
    const description = expense.description || '';
    const amount = util.formatCurrency(expenseLineTotal(expense.quantity, expense.cost), false);
    return `Paid ${amount} to ${payee} on ${expenseDate}: ${description}`;
}

function formatHourlyRatesText(rate: MatterHourlyRates): string {
    const defaultRate = util.formatCurrency(rate.defaultRate || 0, true);
    const user = formatUserNameFromId(rate.id);
    return `${user} rate set to ${defaultRate}`;
}

// --- Entity configs ---

function getContactConfig(): EntityMetaConfig {
    return {
        entityClass: Contact,
        ignoreFields: [
            ...COMMON_IGNORE,
            'employment',
            'demographics',
            'personals',
            'interests',
            'tags',
            'occupations',
        ],
        nameMap: {},
        transforms: {},
        textTransforms: {},
    };
}

function getDocumentConfig(): EntityMetaConfig {
    return {
        entityClass: Document,
        ignoreFields: [
            ...COMMON_IGNORE,
            'buffer',
            'plainText',
            'hash',
            'metadata',
            'service',
            'relatedTo',
        ],
        nameMap: {
            fileName: 'File Name',
            fileSize: 'File Size',
            contentType: 'Content Type',
            protocolVersion: 'Protocol Version',
            privateToUser: 'Private to User',
        },
        transforms: {
            fileSize: (size: number) => {
                if (!size) return '0b';
                if (size < 1024) return `${size}b`;
                if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}k`;
                return `${(size / 1024 / 1024).toFixed(1)}m`;
            },
            tags: (tags: string[]) => tags?.length ? `${tags.length} tag(s)` : null,
        },
        textTransforms: {
            fileSize: (size: number) => {
                if (!size) return '0b';
                if (size < 1024) return `${size}b`;
                if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)}k`;
                return `${(size / 1024 / 1024).toFixed(1)}m`;
            },
            tags: (tags: string[]) => tags?.length ? `${tags.length} tag(s)` : '',
        },
    };
}

function getCampaignConfig(): EntityMetaConfig {
    return {
        entityClass: Campaign,
        ignoreFields: [
            ...COMMON_IGNORE,
            'notes',
            'notesPlain',
            'usesDate',
            'usesCost',
            'leadDateLabel',
        ],
        nameMap: {
            usesPeriod: 'Recurring',
            periodLength: 'Recurring Period',
            periodUnit: 'Recurs Every',
            periodFixedCost: 'Recurring Fixed Cost',
        },
        transforms: {
            cost: util.formatCurrency,
            periodFixedCost: util.formatCurrency,
            hardCosts: (v: CampaignHardCosts) => v ? `${v.item} for $${v.cost} on ${v.incurredOn}` : null,
            recurringCosts: (v: CampaignRecurringCosts) => v ? `${v.startDate} - ${v.endDate} for $${v.cost}` : null,
        },
        textTransforms: {
            cost: (v: any) => util.formatCurrencyAsString(v),
            periodFixedCost: (v: any) => util.formatCurrencyAsString(v),
            hardCosts: (v: CampaignHardCosts) => v ? `${v.item} for $${v.cost} on ${v.incurredOn}` : '',
            recurringCosts: (v: CampaignRecurringCosts) => v ? `${v.startDate} - ${v.endDate} for $${v.cost}` : '',
        },
    };
}

function getLeadConfig(): EntityMetaConfig {
    return {
        entityClass: Lead,
        ignoreFields: [
            ...COMMON_IGNORE,
            'notes',
            'notesPlain',
            'referrals',
        ],
        nameMap: {},
        transforms: {
            caseTypeId: CaseHelper.formatCaseTypeFromId,
            disposition: dispositionText,
            retainerRequested: util.formatCurrency,
            retainerReceived: util.formatCurrency,
            estimatedValue: util.formatCurrency,
            paidConsult: (pc: string) => pc === 't' ? 'True' : pc === 'f' ? 'False' : null,
        },
        textTransforms: {
            caseTypeId: (v: uuid) => CaseHelper.formatCaseTypeFromId(v) || '',
            disposition: dispositionText,
            retainerRequested: (v: any) => util.formatCurrencyAsString(v),
            retainerReceived: (v: any) => util.formatCurrencyAsString(v),
            estimatedValue: (v: any) => util.formatCurrencyAsString(v),
            paidConsult: (pc: string) => pc === 't' ? 'True' : pc === 'f' ? 'False' : '',
        },
        filters: {
            milestones: (ms: Milestone) => !!ms.dateCompleted,
        },
    };
}

function getInvoiceConfig(): EntityMetaConfig {
    return {
        entityClass: Invoice,
        ignoreFields: [
            ...COMMON_IGNORE,
            'taskPositions',
        ],
        nameMap: {
            clientIds: 'Clients',
            matterId: 'Case',
            leadId: 'Lead',
            invoiceStatus: 'Status',
            startDate: 'Period Start',
            endDate: 'Period End',
            issueDate: 'Issue Date',
            dueDate: 'Due Date',
            finalizedOn: 'Finalized On',
            taskIds: 'Tasks',
            expenseIds: 'Expenses',
            transactionIds: 'Transactions',
            flatFeeIds: 'Flat Fees',
            flatFeeMilestoneIds: 'Milestones',
            totalBilled: 'Total Billed',
            totalDue: 'Total Due',
            amountPaid: 'Amount Paid',
            billingIncrement: 'Billing Increment',
            roundingMethod: 'Rounding Method',
            sentDate: 'Sent Date',
            sentUsing: 'Sent Via',
            reconcileNotes: 'Reconcile Notes',
            isReconciled: 'Reconciled',
            includeUnbilled: 'Include Unbilled',
            primaryTax: 'Primary Tax',
            secondaryTax: 'Secondary Tax',
        },
        transforms: {
            invoiceStatus: invoiceStatusText,
            description: (v: string) => v ? TextField.renderHTML(v) : null,
            reconcileNotes: (v: string) => v ? TextField.renderHTML(v) : null,
            totalBilled: util.formatCurrency,
            totalDue: util.formatCurrency,
            amountPaid: util.formatCurrency,
            total: util.formatCurrency,
            billingIncrement: (bi: number) => bi ? util.billingIncrementToFraction(bi) : null,
        },
        textTransforms: {
            invoiceStatus: invoiceStatusText,
            description: (v: string) => v ? stripHTML(v, []) : '',
            reconcileNotes: (v: string) => v ? stripHTML(v, []) : '',
            totalBilled: (v: any) => util.formatCurrencyAsString(v),
            totalDue: (v: any) => util.formatCurrencyAsString(v),
            amountPaid: (v: any) => util.formatCurrencyAsString(v),
            total: (v: any) => util.formatCurrencyAsString(v),
            billingIncrement: (bi: number) => bi ? String(util.billingIncrementToFraction(bi)) : '',
        },
    };
}

function getTransactionConfig(): EntityMetaConfig {
    return {
        entityClass: Transaction,
        ignoreFields: [...COMMON_IGNORE],
        nameMap: {
            txDate: 'Date',
            txType: 'Type',
            invoiceId: 'Invoice',
            invoiceIds: 'Invoices',
            matterIds: 'Cases',
            clientIds: 'Clients',
            processorPaymentMeta: 'Payment Processor',
        },
        transforms: {
            amount: util.formatCurrency,
            txType: (t: string) => t ? i18n.l('transaction', t) : '',
            sourceType: sourceTargetTypeText,
            targetType: sourceTargetTypeText,
            source: getSourceTargetText,
            target: getSourceTargetText,
            processorPaymentMeta: (v: any) => React.createElement(FormatProcessorPaymentMeta, v),
        },
        textTransforms: {
            amount: (v: any) => util.formatCurrencyAsString(v),
            txType: (t: string) => t ? (i18n.ls('transaction', t) || t) : '',
            sourceType: sourceTargetTypeText,
            targetType: sourceTargetTypeText,
            source: getSourceTargetText,
            target: getSourceTargetText,
        },
        keyedObjectFields: {
            processorPaymentMeta: {
                normalize: normalizePaymentMeta,
                nameMap: {
                    id: 'ID',
                    statusText: 'Status',
                    typeText: 'Type',
                    authorization: 'Authorization Code',
                    amount: 'Amount',
                    amount_refunded: 'Amount Refunded',
                    created: 'Created',
                    modified: 'Modified',
                },
                textTransforms: {
                    amount: (v: any) => v != null ? `$${util.numberWithCommas(v)}` : '',
                    amount_refunded: (v: any) => v != null && v > 0 ? `$${util.numberWithCommas(v)}` : '',
                    created: (v: any) => v ? new Date(v).toLocaleString() : '',
                    modified: (v: any) => v ? new Date(v).toLocaleString() : '',
                },
            },
        },
    };
}

function getMatterConfig(entity?: Matter): EntityMetaConfig {
    const ignoreRates: string[] = [];

    if (entity) {
        const rt = entity.rateType || '';
        if (!rt.includes('h')) ignoreRates.push('hourlyRates');
        if (!rt.includes('f')) ignoreRates.push('flatFees');
        if (!rt.includes('c')) ignoreRates.push('contingency');
    }

    return {
        entityClass: Matter,
        ignoreFields: [
            ...COMMON_IGNORE,
            ...ignoreRates,
            'contingency',
            'activityTypeOverrides',
            'escrows',
            'notes',
            'notesPlain',
        ],
        nameMap: {
            caseTypeId: 'Case Type',
            leadId: 'Lead',
            rateType: 'Rate Types',
        },
        transforms: {
            disposition: dispositionText,
            expenses: formatExpenseText,
            hourlyRates: formatHourlyRatesText,
            minimumBalance: util.formatCurrency,
            minimumTrust: util.formatCurrency,
            rateType: (r: RateTypeType) => parseRateTypesText(r),
            leadId: (lid: uuid) => LeadHelper.getLeadFromId(lid)?.name,
            court: (lid: uuid) => LookupRegistry.getLookupById(lid)?.key,
            billingIncrement: (bi: number) => bi ? util.billingIncrementToFraction(bi) : null,
        },
        textTransforms: {
            disposition: dispositionText,
            expenses: formatExpenseText,
            hourlyRates: formatHourlyRatesText,
            minimumBalance: (v: any) => util.formatCurrencyAsString(v),
            minimumTrust: (v: any) => util.formatCurrencyAsString(v),
            rateType: parseRateTypesText,
            leadId: (lid: uuid) => LeadHelper.getLeadFromId(lid)?.name || '',
            court: (lid: uuid) => LookupRegistry.getLookupById(lid)?.key || '',
            billingIncrement: (bi: number) => bi ? String(util.billingIncrementToFraction(bi)) : '',
        },
    };
}

function getTaskConfig(): EntityMetaConfig {
    return {
        entityClass: Task,
        ignoreFields: [...COMMON_IGNORE, 'notes', 'notesPlain', 'fromTemplateId'],
        nameMap: {
            activityTypeIds: 'Activities',
            duration: 'Estimated Time',
            invoiceId: 'Invoice',
            invoiceNotes: 'Billable Description',
            relatedTo: 'Participants',
            tieIns: 'Category Relationship',
            docketId: 'Docket',
        },
        transforms: {
            associatedRate: util.formatCurrency,
            laborRate: util.formatCurrency,
            travelRate: util.formatCurrency,
            cost: util.formatCurrency,
            costOverride: util.formatCurrency,
            elapsedTime: (v: number) => formatDuration(v, 'hh:mm:ss'),
            impact: impactUrgencyText,
            urgency: impactUrgencyText,
            docketId: (did: uuid) => store.getState().dockets.get(did)?.name,
            assignedTo: (v: uuid) => {
                const name = formatUserNameFromId(v);
                if (name) return name;
                return store.getState().groups.find(g => g.id === v)?.name || v;
            },
            duration: (v: number) => v > 0 ? `${v} hour(s)` : '',
            laborTime: EntityMetaHelper.formatDuration,
            laborTimeOverride: EntityMetaHelper.formatDuration,
            travelTime: EntityMetaHelper.formatDuration,
            travelTimeOverride: EntityMetaHelper.formatDuration,
            invoiceNotes: (v: string) => v || '',
            lastStart: (v: number) => v === 0 ? null : DateUtil.formatDateTime(v),
            notification: (v: TaskNotification, before: TaskNotification, after: TaskNotification) => {
                if (!v) return null;
                const parts: string[] = [];
                if (before?.to !== after?.to) parts.push(`To: ${formatUserNameFromId(v.to)}`);
                if (before?.from !== after?.from) parts.push(`From: ${formatUserNameFromId(v.from)}`);
                if (before?.status !== after?.status) {
                    parts.push(`Status: ${v.status === 'r' ? 'Review' : 'Completed'}`);
                }
                if (before?.message !== after?.message && v.message) parts.push(v.message);
                return parts.join(', ');
            },
            tieIns: (v: TaskTieIns) => {
                if (!v) return null;
                const {module, entity, id} = v;
                if (module === 'Client' && entity === 'Matter') {
                    return CaseHelper.getMatterFromId(id)?.name || '(deleted)';
                }
                if (module === 'Sales' && entity === 'Lead') {
                    return LeadHelper.getLeadFromId(id)?.name || '(deleted)';
                }
                if (module === 'Marketing' && entity === 'Campaign') {
                    return CampaignHelper.getCampaignFromId(id)?.name || '(deleted)';
                }
                return JSON.stringify(v);
            },
        },
        textTransforms: {
            associatedRate: (v: any) => util.formatCurrencyAsString(v),
            laborRate: (v: any) => util.formatCurrencyAsString(v),
            travelRate: (v: any) => util.formatCurrencyAsString(v),
            cost: (v: any) => util.formatCurrencyAsString(v),
            costOverride: (v: any) => util.formatCurrencyAsString(v),
            elapsedTime: (v: number) => formatDuration(v, 'hh:mm:ss'),
            impact: impactUrgencyText,
            urgency: impactUrgencyText,
            docketId: (did: uuid) => store.getState().dockets.get(did)?.name || '',
            assignedTo: (v: uuid) => {
                const name = formatUserNameFromId(v);
                if (name) return name;
                return store.getState().groups.find(g => g.id === v)?.name || '';
            },
            duration: (v: number) => v > 0 ? `${v} hour(s)` : '',
            laborTime: (v: number) => EntityMetaHelper.formatDuration(v),
            laborTimeOverride: (v: number) => EntityMetaHelper.formatDuration(v),
            travelTime: (v: number) => EntityMetaHelper.formatDuration(v),
            travelTimeOverride: (v: number) => EntityMetaHelper.formatDuration(v),
            invoiceNotes: (v: string) => v || '',
            lastStart: (v: number) => v === 0 ? '' : DateUtil.formatDateTime(v),
            notification: (v: TaskNotification) => {
                if (!v) return '';
                const parts: string[] = [];
                if (v.to) parts.push(`To: ${formatUserNameFromId(v.to)}`);
                if (v.from) parts.push(`From: ${formatUserNameFromId(v.from)}`);
                if (v.status) parts.push(`Status: ${v.status === 'r' ? 'Review' : 'Completed'}`);
                return parts.join(', ');
            },
            tieIns: (v: TaskTieIns) => {
                if (!v) return '';
                const {module, entity, id} = v;
                if (module === 'Client' && entity === 'Matter') {
                    return CaseHelper.getMatterFromId(id)?.name || '(deleted)';
                }
                if (module === 'Sales' && entity === 'Lead') {
                    return LeadHelper.getLeadFromId(id)?.name || '(deleted)';
                }
                if (module === 'Marketing' && entity === 'Campaign') {
                    return CampaignHelper.getCampaignFromId(id)?.name || '(deleted)';
                }
                return '';
            },
        },
    };
}

// --- Registry ---

const CONFIG_MAP: Record<string, (entity?: any) => EntityMetaConfig> = {
    contact: getContactConfig,
    campaign: getCampaignConfig,
    document: getDocumentConfig,
    lead: getLeadConfig,
    invoice: getInvoiceConfig,
    transaction: getTransactionConfig,
    matter: getMatterConfig,
    task: getTaskConfig,
};

/**
 * Get the entity configuration for a given entity name.
 * Pass the entity instance for configs that depend on entity state (e.g., matter's rateType).
 */
export function getEntityConfig(entityName: string, entity?: any): EntityMetaConfig | null {
    const factory = CONFIG_MAP[entityName];
    if (!factory) return null;
    return factory(entity);
}

/**
 * Get a list of all registered entity names.
 */
export function getRegisteredEntityNames(): string[] {
    return Object.keys(CONFIG_MAP);
}
